import React, { useState } from "react";
import { CreateCommanWay, ListCommanWay, UpdateCommanWay } from "API/commonApi";
import { useQuery } from "@tanstack/react-query";
import Swal from "sweetalert2";
import UseToast from "utils/AfToast";
import Select from "react-select";
import Spinner from "utils/Spinner";
import moment from "moment";
import { Button, Checkbox } from "@heroui/react";
import {
  DocumentDuplicateIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowPathIcon,
  BuildingOfficeIcon,
  NoSymbolIcon,
} from "@heroicons/react/24/outline";
import { roles } from "utils/constant";

const pickupSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    background: "rgba(255, 255, 255, 0.95)",
    borderRadius: "10px",
    borderColor: state.isFocused ? "#0077B6" : "#cbd5e1",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(0, 119, 182, 0.2)" : "none",
    padding: "0px 4px",
    minHeight: "36px",
    fontSize: "13px",
    transition: "all 0.2s ease",
    "&:hover": { borderColor: "#0077B6" },
  }),
  valueContainer: (base: any) => ({ ...base, padding: "0 8px" }),
  input: (base: any) => ({ ...base, margin: "0px", padding: "0px" }),
  indicatorsContainer: (base: any) => ({ ...base, height: "36px" }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected ? "#0077B6" : state.isFocused ? "#e0f2fe" : "white",
    color: state.isSelected ? "white" : "#1e293b",
    fontSize: "13px",
    padding: "6px 12px",
    cursor: "pointer",
  }),
  menu: (base: any) => ({
    ...base,
    borderRadius: "10px",
    overflow: "hidden",
    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)",
    zIndex: 99999,
  }),
  menuPortal: (base: any) => ({ ...base, zIndex: 99999 }),
  placeholder: (base: any) => ({ ...base, color: "#94a3b8", fontSize: "13px" }),
  singleValue: (base: any) => ({ ...base, color: "#1e293b", fontSize: "13px", fontWeight: "500" }),
};

const getFinancialYear = (date?: any) => {
  const m = date ? moment(date) : moment();
  const year = m.year();
  const month = m.month();
  if (month >= 3) {
    return `${year}-${year + 1}`;
  } else {
    return `${year - 1}-${year}`;
  }
};

const getFinancialYearOptions = () => {
  const currentFY = getFinancialYear();
  const currentStartYear = parseInt(currentFY.split("-")[0]);
  return [
    { value: `${currentStartYear + 1}-${currentStartYear + 2}`, label: `FY ${currentStartYear + 1}-${currentStartYear + 2}` },
    { value: `${currentStartYear}-${currentStartYear + 1}`, label: `FY ${currentStartYear}-${currentStartYear + 1} (Current)` },
    { value: `${currentStartYear - 1}-${currentStartYear}`, label: `FY ${currentStartYear - 1}-${currentStartYear}` },
    { value: `${currentStartYear - 2}-${currentStartYear - 1}`, label: `FY ${currentStartYear - 2}-${currentStartYear - 1}` },
  ];
};

const LeavePolicies: React.FC = () => {
  const userD = (() => {
    const auth = localStorage.getItem("auth");
    return auth ? JSON.parse(auth) : {};
  })();
  const userDetails = userD?.state?.user || {};

  // Strictly SuperAdmin only
  if (userDetails.roleName !== roles.SUPER_ADMIN) {
    return (
      <div className="p-8 text-center">
        <div className="max-w-md mx-auto bg-white p-6 rounded-2xl border border-red-100 shadow-sm text-center">
          <NoSymbolIcon className="w-12 h-12 text-red-500 mx-auto mb-2" />
          <h2 className="text-base font-bold text-slate-800">Access Denied</h2>
          <p className="text-xs text-slate-500 mt-1">This page is restricted to Super Admin only.</p>
        </div>
      </div>
    );
  }

  const [selectedFY, setSelectedFY] = useState<any>({
    value: getFinancialYear(),
    label: `FY ${getFinancialYear()} (Current)`,
  });

  const [subbranchOptions, setSubbranchOptions] = useState<any[]>([]);
  const [selectedSubbranch, setSelectedSubbranch] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const perPage = 20;

  // Create / Edit Modal state
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editItem, setEditItem] = useState<any>(null);
  const [formValues, setFormValues] = useState<any>({
    name: "",
    financialYear: getFinancialYear(),
    description: "",
    subbranch: null,
    casualLeave: 12,
    sickLeave: 6,
    paidLeave: 15,
    maternityLeave: 0,
    allowMaternity: false,
    allowCompOff: true,
    maxCarryForward: 0,
  });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Fetch subbranches
  useQuery(
    ["subbranchListForPolicies"],
    () => ListCommanWay("/admin/subbranch/list", {}, { paginate: 1000 }),
    {
      onSuccess: (res: any) => {
        if (res?.status === "SUCCESS") {
          const list = res?.data?.data || res?.data?.docs || [];
          setSubbranchOptions(list.map((b: any) => ({ value: b.id, label: b.branch_name || b.name || `#${b.id}` })));
        }
      },
    }
  );

  // Build filter query
  const buildQuery = () => {
    const q: any = {};
    if (selectedFY?.value) q.financialYear = selectedFY.value;
    if (selectedSubbranch?.value) q.subbranch = selectedSubbranch.value;
    return q;
  };

  // Fetch policies list
  const {
    data: policiesData,
    isLoading,
    isFetching,
    refetch,
  } = useQuery(
    ["leavePoliciesList", selectedFY?.value, selectedSubbranch?.value, currentPage],
    () =>
      ListCommanWay("/admin/leave-policy/list", buildQuery(), {
        page: currentPage,
        paginate: perPage,
        sort: { createdAt: -1 },
      }),
    {
      keepPreviousData: true,
    }
  );

  const policies = policiesData?.data?.data || policiesData?.data?.docs || [];
  const paginator = policiesData?.data?.paginator || {
    itemCount: policies.length,
    pageCount: Math.ceil(policies.length / perPage) || 1,
    currentPage: 1,
  };

  // Open Create Modal
  const handleOpenCreate = () => {
    setEditItem(null);
    setFormValues({
      name: "",
      financialYear: selectedFY?.value || getFinancialYear(),
      description: "",
      subbranch: selectedSubbranch || null,
      casualLeave: 12,
      sickLeave: 6,
      paidLeave: 15,
      maternityLeave: 0,
      allowMaternity: false,
      allowCompOff: true,
      maxCarryForward: 0,
    });
    setIsModalOpen(true);
  };

  // Open Edit Modal
  const handleOpenEdit = (item: any) => {
    setEditItem(item);
    const subbranchVal = item.subbranch ?? item.branch_name ?? item.branchId;
    const subbranchOpt = subbranchOptions.find((b) => b.value === subbranchVal);
    const subbranchLabel = item._subbranch?.branch_name || item._subbranch?.name || item._branch?.name || (subbranchVal ? `Subbranch #${subbranchVal}` : "All Subbranches");
    setFormValues({
      name: item.name || "",
      financialYear: item.financialYear || getFinancialYear(),
      description: item.description || "",
      subbranch: subbranchOpt || (subbranchVal ? { value: subbranchVal, label: subbranchLabel } : null),
      casualLeave: item.casualLeave ?? item.casualTotal ?? 12,
      sickLeave: item.sickLeave ?? item.sickTotal ?? 6,
      paidLeave: item.paidLeave ?? item.paidTotal ?? 15,
      maternityLeave: item.maternityLeave ?? item.maternityTotal ?? 0,
      allowMaternity: item.allowMaternity !== undefined ? Boolean(item.allowMaternity) : (item.maternityLeave > 0 || item.maternityTotal > 0),
      allowCompOff: item.allowCompOff !== undefined ? Boolean(item.allowCompOff) : item.isCompOffAllowed !== false,
      maxCarryForward: item.maxCarryForward ?? 0,
    });
    setIsModalOpen(true);
  };

  // Submit Policy Form
  const handleSubmit = async (e?: any) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    if (!formValues.name?.trim()) {
      UseToast("Please enter policy name", "warning");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        name: formValues.name.trim(),
        financialYear: formValues.financialYear || getFinancialYear(),
        description: formValues.description?.trim() || "",
        subbranch: formValues.subbranch?.value || null,
        casualLeave: Number(formValues.casualLeave),
        sickLeave: Number(formValues.sickLeave),
        paidLeave: Number(formValues.paidLeave),
        maternityLeave: Number(formValues.maternityLeave || 0),
        allowMaternity: Boolean(formValues.allowMaternity),
        allowCompOff: Boolean(formValues.allowCompOff),
        maxCarryForward: Number(formValues.maxCarryForward || 0),
      };

      let res: any;
      if (editItem?.id) {
        res = await UpdateCommanWay({
          url: `/admin/leave-policy/update/${editItem.id}`,
          data: payload,
        });
      } else {
        res = await CreateCommanWay({
          url: "/admin/leave-policy/create",
          data: payload,
        });
      }

      if (res?.status === "SUCCESS") {
        UseToast(editItem ? "Leave policy updated!" : "Leave policy created!", "success");
        setIsModalOpen(false);
        refetch();
      } else {
        UseToast(res?.message || "Failed to save policy", "error");
      }
    } catch (err: any) {
      UseToast(err?.message || "Error saving policy", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Soft Delete Policy
  const handleDeletePolicy = (item: any) => {
    Swal.fire({
      title: `Delete Policy "${item.name}"?`,
      text: "Are you sure you want to remove this policy template?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, Delete",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res: any = await UpdateCommanWay({
            url: `/admin/leave-policy/softDelete/${item.id}`,
            data: {},
          });
          if (res?.status === "SUCCESS") {
            UseToast("Policy deleted successfully", "success");
            refetch();
          } else {
            UseToast(res?.message || "Failed to delete policy", "error");
          }
        } catch (err: any) {
          UseToast(err?.message || "Error deleting policy", "error");
        }
      }
    });
  };

  return (
    <div className="bg-gradient-to-br from-purple-400 via-blue-50 to-primary/50 py-2 px-2 font-inter min-h-screen">
      {(isLoading || isFetching) && <Spinner />}

      {/* Header Bar */}
      <div className="mx-auto mb-2">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-3 py-2 shadow-xl shadow-slate-200/50">
          <div className="flex items-center justify-between">
            <div className="bg-blue-100 text-primary px-3 py-1 rounded-lg text-sm font-semibold">
              Total: {paginator.itemCount} Policies
            </div>

            <h1 className="text-base font-bold bg-gradient-to-r from-primary to-amber-600 bg-clip-text text-transparent uppercase">
              LEAVE POLICIES
            </h1>

            <div className="flex items-center gap-2">
              <div className="w-44">
                <Select
                  options={getFinancialYearOptions()}
                  value={selectedFY}
                  onChange={setSelectedFY}
                  styles={pickupSelectStyles}
                  isSearchable={false}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                />
              </div>

              <Button
                size="sm"
                variant="bordered"
                isIconOnly
                onPress={() => refetch()}
                className="h-8 w-8 bg-white/90 rounded-lg text-slate-700"
                title="Refresh List"
              >
                <ArrowPathIcon className="w-4 h-4" />
              </Button>

              <Button
                size="sm"
                color="primary"
                onPress={handleOpenCreate}
                startContent={<PlusIcon className="w-4 h-4" />}
                className="h-8 px-3 text-xs font-semibold rounded-lg bg-primary text-white shadow hover:bg-primary/90"
              >
                Create Policy
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Card */}
      <div className="mx-auto mb-3">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-5 py-4 shadow-xl shadow-slate-200/50">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-64">
              <label className="block mb-1 text-xs font-semibold text-slate-700">Subbranch / Location</label>
              <Select
                options={[{ value: null, label: "All Subbranches (Default)" }, ...subbranchOptions]}
                value={selectedSubbranch}
                onChange={(e) => {
                  setSelectedSubbranch(e);
                  setCurrentPage(1);
                }}
                placeholder="Filter by Subbranch"
                isClearable
                styles={pickupSelectStyles}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div className="mx-auto mb-3">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl shadow-slate-200/50 p-4 overflow-hidden">
          {policies.length === 0 ? (
            <div className="p-12 text-center text-slate-400">
              <DocumentDuplicateIcon className="w-14 h-14 mx-auto text-slate-300 mb-2 stroke-1" />
              <p className="text-base font-semibold text-slate-600">No policy templates found</p>
              <p className="text-xs text-slate-400 mt-1">Click "Create Policy" to define your first leave policy.</p>
              <Button
                size="sm"
                color="primary"
                variant="flat"
                onPress={handleOpenCreate}
                className="mt-3 text-xs font-bold"
              >
                Create Policy
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[70vh]">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-100/90 text-slate-800 font-bold uppercase tracking-wider text-xs sticky top-0 z-10 backdrop-blur-sm border-b border-slate-200">
                  <tr>
                    <th className="py-3 px-4">Policy Name & FY</th>
                    <th className="py-3 px-4">Subbranch / Location</th>
                    <th className="py-3 px-4 text-center">Casual (CL)</th>
                    <th className="py-3 px-4 text-center">Sick (SL)</th>
                    <th className="py-3 px-4 text-center">Paid (PL)</th>
                    <th className="py-3 px-4 text-center">Maternity</th>
                    <th className="py-3 px-4 text-center">Comp-Off</th>
                    <th className="py-3 px-4 text-center">Max Rollover</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-800">
                  {policies.map((item: any) => {
                    const subbranchName = item._subbranch?.branch_name || item._subbranch?.name || item._branch?.name || item._branch?.branch_name || (item.subbranch ? `Subbranch #${item.subbranch}` : "All Subbranches (Default)");
                    const subbranchCode = item._subbranch?.branch_code || item._subbranch?.branchCode || item._branch?.branchCode;
                    return (
                      <tr key={item.id} className="hover:bg-blue-50/50 transition-colors">
                        <td className="py-3 px-4 whitespace-nowrap">
                          <div className="font-bold text-slate-900 text-sm">{item.name}</div>
                          <div className="text-xs text-slate-500 font-medium">{item.financialYear || "FY Annual"}</div>
                          {item.description && (
                            <p className="text-xs text-slate-500 font-medium line-clamp-1 mt-0.5">{item.description}</p>
                          )}
                        </td>

                        <td className="py-3 px-4 whitespace-nowrap text-slate-700 font-medium">
                          <div className="flex items-center gap-1.5">
                            <BuildingOfficeIcon className="w-4 h-4 text-slate-400" />
                            <span>{subbranchName}</span>
                            {subbranchCode && (
                              <span className="text-[10px] bg-slate-100 text-slate-600 font-bold px-1.5 py-0.5 rounded">
                                {subbranchCode}
                              </span>
                            )}
                          </div>
                        </td>

                        <td className="py-3 px-4 text-center font-bold text-blue-950 text-base">
                          {item.casualLeave ?? item.casualTotal ?? 12} <span className="text-xs font-normal text-slate-400">Days</span>
                        </td>

                        <td className="py-3 px-4 text-center font-bold text-emerald-950 text-base">
                          {item.sickLeave ?? item.sickTotal ?? 6} <span className="text-xs font-normal text-slate-400">Days</span>
                        </td>

                        <td className="py-3 px-4 text-center font-bold text-purple-950 text-base">
                          {item.paidLeave ?? item.paidTotal ?? 15} <span className="text-xs font-normal text-slate-400">Days</span>
                        </td>

                        <td className="py-3 px-4 text-center">
                          {item.allowMaternity || item.maternityLeave > 0 ? (
                            <div>
                              <strong className="text-pink-950 text-base font-bold">{item.maternityLeave ?? item.maternityTotal ?? 0}</strong>
                              <span className="text-xs font-normal text-slate-400"> Days</span>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400 font-medium">Disabled</span>
                          )}
                        </td>

                        <td className="py-3 px-4 text-center">
                          {item.allowCompOff !== false && item.isCompOffAllowed !== false ? (
                            <span className="px-2.5 py-1 bg-amber-50 text-amber-800 border border-amber-200 rounded-full text-xs font-bold">
                              Allowed
                            </span>
                          ) : (
                            <span className="px-2.5 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">
                              Disabled
                            </span>
                          )}
                        </td>

                        <td className="py-3 px-4 text-center text-slate-700 font-semibold text-xs">
                          {item.maxCarryForward ? `${item.maxCarryForward} Days` : "None"}
                        </td>

                        <td className="py-3 px-4 text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-1.5">
                            <Button
                              size="sm"
                              variant="light"
                              isIconOnly
                              onPress={() => handleOpenEdit(item)}
                              className="h-8 w-8 text-blue-700 hover:bg-blue-100 rounded-lg"
                              title="Edit Policy"
                            >
                              <PencilSquareIcon className="w-4 h-4" />
                            </Button>

                            <Button
                              size="sm"
                              variant="light"
                              isIconOnly
                              onPress={() => handleDeletePolicy(item)}
                              className="h-8 w-8 text-red-600 hover:bg-red-100 rounded-lg"
                              title="Delete Policy"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create / Edit Policy Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-100">
            <div className="px-6 py-4 bg-gradient-to-r from-primary to-primary2 text-white flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <DocumentDuplicateIcon className="w-5 h-5 text-blue-200" />
                <h3 className="text-base font-bold">{editItem ? "Edit Leave Policy" : "Create Leave Policy"}</h3>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-1 rounded-lg text-blue-200 hover:text-white hover:bg-white/10"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4 text-sm">
              <div>
                <label className="block text-slate-700 font-bold mb-1 text-xs">
                  Policy Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formValues.name}
                  onChange={(e) => setFormValues({ ...formValues, name: e.target.value })}
                  placeholder="e.g. Delhi Corporate Hub Policy FY26-27"
                  className="w-full h-9 px-3 text-sm border border-slate-300 rounded-xl outline-none focus:border-blue-600 font-semibold"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-bold mb-1 text-xs">Target Financial Year</label>
                  <Select
                    options={getFinancialYearOptions()}
                    value={{ value: formValues.financialYear, label: `FY ${formValues.financialYear}` }}
                    onChange={(e: any) => setFormValues({ ...formValues, financialYear: e?.value })}
                    styles={pickupSelectStyles}
                    isSearchable={false}
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    menuPosition="fixed"
                  />
                </div>

                <div>
                  <label className="block text-slate-700 font-bold mb-1 text-xs">Target Subbranch</label>
                  <Select
                    options={[{ value: null, label: "All Subbranches (Default)" }, ...subbranchOptions]}
                    value={formValues.subbranch}
                    onChange={(e) => setFormValues({ ...formValues, subbranch: e })}
                    placeholder="All Subbranches"
                    isClearable
                    styles={pickupSelectStyles}
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    menuPosition="fixed"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-700 font-bold mb-1 text-xs">Description</label>
                <textarea
                  rows={2}
                  value={formValues.description}
                  onChange={(e) => setFormValues({ ...formValues, description: e.target.value })}
                  placeholder="Brief description of this policy template..."
                  className="w-full p-2.5 text-sm border border-slate-300 rounded-xl outline-none focus:border-blue-600 resize-none font-medium"
                />
              </div>

              {/* Leave Numbers */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-slate-700 font-bold mb-1 text-xs">Casual (CL)</label>
                  <input
                    type="number"
                    min={0}
                    value={formValues.casualLeave}
                    onChange={(e) => setFormValues({ ...formValues, casualLeave: e.target.value })}
                    className="w-full h-9 px-3 text-sm border border-slate-300 rounded-xl outline-none focus:border-blue-600 font-bold"
                    required
                  />
                </div>

                <div>
                  <label className="block text-slate-700 font-bold mb-1 text-xs">Sick (SL)</label>
                  <input
                    type="number"
                    min={0}
                    value={formValues.sickLeave}
                    onChange={(e) => setFormValues({ ...formValues, sickLeave: e.target.value })}
                    className="w-full h-9 px-3 text-sm border border-slate-300 rounded-xl outline-none focus:border-blue-600 font-bold"
                    required
                  />
                </div>

                <div>
                  <label className="block text-slate-700 font-bold mb-1 text-xs">Paid (PL)</label>
                  <input
                    type="number"
                    min={0}
                    value={formValues.paidLeave}
                    onChange={(e) => setFormValues({ ...formValues, paidLeave: e.target.value })}
                    className="w-full h-9 px-3 text-sm border border-slate-300 rounded-xl outline-none focus:border-blue-600 font-bold"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-slate-700 font-bold mb-1 text-xs">Maternity Leave Days</label>
                  <input
                    type="number"
                    min={0}
                    value={formValues.maternityLeave}
                    onChange={(e) => setFormValues({ ...formValues, maternityLeave: e.target.value, allowMaternity: Number(e.target.value) > 0 })}
                    className="w-full h-9 px-3 text-sm border border-slate-300 rounded-xl outline-none focus:border-blue-600 font-bold"
                  />
                </div>

                <div>
                  <label className="block text-slate-700 font-bold mb-1 text-xs">Max Carry Forward</label>
                  <input
                    type="number"
                    min={0}
                    value={formValues.maxCarryForward}
                    onChange={(e) => setFormValues({ ...formValues, maxCarryForward: e.target.value })}
                    className="w-full h-9 px-3 text-sm border border-slate-300 rounded-xl outline-none focus:border-blue-600 font-bold"
                  />
                </div>

                <div className="flex items-center pt-5">
                  <Checkbox
                    isSelected={formValues.allowCompOff}
                    onValueChange={(val) => setFormValues({ ...formValues, allowCompOff: val })}
                    size="md"
                    color="primary"
                  >
                    <span className="text-sm font-bold text-slate-800">Comp-Off Allowed</span>
                  </Checkbox>
                </div>
              </div>

              <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2.5">
                <Button
                  size="sm"
                  variant="flat"
                  onPress={() => setIsModalOpen(false)}
                  isDisabled={isSubmitting}
                  className="text-xs font-semibold"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  color="primary"
                  type="submit"
                  onPress={() => handleSubmit()}
                  isLoading={isSubmitting}
                  className="text-xs font-bold bg-primary text-white"
                >
                  {editItem ? "Save Policy" : "Create Policy"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeavePolicies;
